import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  History,
  Loader2,
  Plus,
  Save,
  ShieldCheck,
  UserCog,
  UsersRound
} from 'lucide-react';
import RbacModal from '../../components/admin/RbacModal';
import { employerRbacAPI } from '../../services/api';

const emptyUserForm = {
  firstName: '',
  lastName: '',
  email: '',
  roleId: '',
  status: 'active'
};

const extractError = (error, fallback) =>
  error?.message || error?.error || error?.response?.error || fallback;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const labelize = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

function UsersPage() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [form, setForm] = useState(emptyUserForm);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectableRoles = useMemo(
    () => roles.filter((role) => !role.isSuperAdmin),
    [roles]
  );
  const createRoleOptions = selectableRoles.length ? selectableRoles : roles;
  const isEditing = Boolean(selectedUser);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesResponse, usersResponse, auditResponse] = await Promise.all([
        employerRbacAPI.getRoles(),
        employerRbacAPI.getUsers(),
        employerRbacAPI.getAuditLogs({ limit: 40 })
      ]);
      setRoles(rolesResponse.roles || []);
      setUsers(usersResponse.users || []);
      setAuditLogs(auditResponse.auditLogs || []);
    } catch (error) {
      toast.error(extractError(error, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const closeEditor = () => {
    if (saving) return;
    setEditorOpen(false);
    setSelectedUser(null);
    setForm(emptyUserForm);
  };

  const startCreate = () => {
    setSelectedUser(null);
    setForm({
      ...emptyUserForm,
      roleId: createRoleOptions[0]?.id || ''
    });
    setEditorOpen(true);
  };

  const startEdit = (user) => {
    setSelectedUser(user);
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      roleId: user.role?.id || '',
      status: user.membership?.status === 'disabled' ? 'disabled' : 'active'
    });
    setEditorOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        roleId: Number(form.roleId)
      };

      if (isEditing) {
        await employerRbacAPI.updateUser(selectedUser.id, {
          ...payload,
          status: form.status
        });
        toast.success('User updated.');
      } else {
        await employerRbacAPI.createUser({
          ...payload,
          email: form.email.trim()
        });
        toast.success('User created. Password setup email sent.');
      }

      setEditorOpen(false);
      setSelectedUser(null);
      setForm(emptyUserForm);
      await loadData();
    } catch (error) {
      toast.error(
        extractError(
          error,
          isEditing ? 'User update failed.' : 'User creation failed.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6 pt-16 sm:px-6 sm:pt-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-[#eef9ff] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
              <UsersRound size={14} /> Access control
            </p>
            <h1 className="mt-3 font-display text-3xl font-normal text-[#0b1f3a]">
              Admin RBAC
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Manage employer dashboard users from one table and open a focused
              editor only when access needs to change.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Link
              to="/roles"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-[#0b1f3a] shadow-sm transition hover:border-sky-200 hover:bg-[#eef9ff]"
            >
              <ShieldCheck size={16} />
              Roles
            </Link>
            <button
              type="button"
              onClick={startCreate}
              disabled={!roles.length}
              title={
                roles.length
                  ? 'Add dashboard user'
                  : 'Create a role before adding users'
              }
              className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={16} />
              Add user
            </button>
          </div>
        </header>

        <section className="overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="font-display text-lg font-normal text-[#0b1f3a]">
                Dashboard users
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                {users.length} users on this employer account
              </p>
            </div>
            {!roles.length && !loading && (
              <p className="text-xs text-amber-700">
                No roles available.{' '}
                <Link to="/roles" className="font-semibold underline">
                  Create a role
                </Link>{' '}
                first.
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading users
            </div>
          ) : users.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/80 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Two-factor auth</th>
                    <th className="px-5 py-3">Last login</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((user) => {
                    const disabled = user.membership?.status === 'disabled';
                    return (
                      <tr
                        key={user.id}
                        className="transition hover:bg-slate-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#0b1f3a]">
                            {user.name || user.email}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {user.email}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                            {user.role?.name || 'No role'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              disabled
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-emerald-50 text-emerald-700'
                            }`}
                          >
                            {disabled ? 'Disabled' : 'Active'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-slate-600">
                          {user.email2faEnabled ? 'Enabled' : 'Off'}
                        </td>
                        <td className="px-5 py-4 text-slate-600">
                          {formatDateTime(user.lastLoginAt)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => startEdit(user)}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-[#0b1f3a] transition hover:border-sky-200 hover:bg-[#eef9ff]"
                          >
                            <UserCog size={15} />
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-12 text-center">
              <UsersRound className="mx-auto text-slate-300" size={30} />
              <p className="mt-3 text-sm font-medium text-slate-700">
                No dashboard users yet
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Add a user to assign role-based dashboard access.
              </p>
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-4 sm:px-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-[#0b1f3a]">
              <History size={17} />
            </span>
            <div>
              <h2 className="font-display text-lg font-normal text-[#0b1f3a]">
                Audit log
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Recent authenticated and access-control activity
              </p>
            </div>
          </div>

          {auditLogs.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-[820px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/80 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Actor</th>
                    <th className="px-5 py-3">Activity</th>
                    <th className="px-5 py-3">Endpoint</th>
                    <th className="px-5 py-3 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="transition hover:bg-slate-50/60"
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-[#0b1f3a]">
                          {log.actorName || 'System'}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {log.actorEmail}
                        </div>
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-700">
                        {labelize(log.action)}
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-500">
                        {[log.method, log.path].filter(Boolean).join(' ') ||
                          '-'}
                      </td>
                      <td className="px-5 py-4 text-right text-xs text-slate-500">
                        {formatDateTime(log.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                No audit events recorded yet.
              </div>
            )
          )}
        </section>
      </div>

      <RbacModal
        open={editorOpen}
        onClose={closeEditor}
        eyebrow="Admin RBAC"
        title={isEditing ? 'Manage user access' : 'Add dashboard user'}
        description={
          isEditing
            ? 'Update the selected user without changing the rest of the user table.'
            : 'Create a dashboard user and assign the initial role. A password setup code is sent by email.'
        }
        footer={
          <>
            <div />
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                onClick={closeEditor}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-editor-form"
                disabled={
                  saving ||
                  !form.firstName.trim() ||
                  !form.lastName.trim() ||
                  !form.roleId ||
                  (!isEditing && !form.email.trim())
                }
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 text-sm font-medium text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isEditing ? (
                  <Save size={16} />
                ) : (
                  <Plus size={16} />
                )}
                {isEditing ? 'Save changes' : 'Add user'}
              </button>
            </div>
          </>
        }
      >
        <form
          id="user-editor-form"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                First name
              </span>
              <input
                value={form.firstName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    firstName: event.target.value
                  }))
                }
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                required
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Last name
              </span>
              <input
                value={form.lastName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    lastName: event.target.value
                  }))
                }
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                required
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">
              Email
            </span>
            <input
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  email: event.target.value
                }))
              }
              className={`h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none transition ${
                isEditing
                  ? 'bg-slate-50 text-slate-500'
                  : 'bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100'
              }`}
              placeholder="person@company.com"
              disabled={isEditing}
              required={!isEditing}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Role
              </span>
              <select
                value={form.roleId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    roleId: event.target.value
                  }))
                }
                disabled={Boolean(selectedUser?.role?.isSuperAdmin)}
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-500"
                required
              >
                {(isEditing ? roles : createRoleOptions).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>

            {isEditing ? (
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">
                  Status
                </span>
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      status: event.target.value
                    }))
                  }
                  className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">
                The user starts active and receives a password setup email after
                creation.
              </div>
            )}
          </div>
        </form>
      </RbacModal>
    </div>
  );
}

export default UsersPage;
